import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  X,
  Save,
  Eye,
  Code2,
  Plus,
  Database,
  Trash2,
  Pencil,
  LayoutTemplate,
  ChevronRight,
  Copy,
  Check,
  Layers,
  Braces,
  Settings2,
  LayoutGrid,
  Bot,
  Sparkles,
  Maximize2,
  Minimize2,
  Loader2,
  AlertCircle,
  UploadCloud,
} from 'lucide-react';
import type { Theme, ThemeColors, ReportEntity, ReportDefinition, ReportSettings } from './types';
import EntityModal from './EntityModal';
import ReportSettingsModal, { createDefaultReportSettings } from './ReportSettingsModal';
import ReportTemplatePickerModal from './ReportTemplatePickerModal';
import ReportCodeEditor, { type ReportCodeEditorHandle } from './ReportCodeEditor';
import ReportCopilotPanel from './ReportCopilotPanel';
import {
  analyticsHubService,
  mapAnalyticsHubEntity,
  buildReportDefinitionUpsertPayload,
  resolveReportDbIdFromUpsertResponse,
  resolveJsxToTsxConversion,
} from '../../../../../components/api/analyticsHubService';
import { useToast } from '../../../../../components/common/Toast';
import { type ReportTemplate } from './reportTemplates';
import {
  mapAnalyticsHubReportDefinition,
  templatePartsFromAnalyticsHubReport,
} from './reportApiMapper';
import { combineTemplateParts, splitTemplate, type TemplateParts } from './templateParts';
import { flattenEntityPaths, formatEntityPlaceholder, getEntityColor, getReportAnalyticsHubViewUrl, buildReportPreviewHtml } from './utils';
import './reportDesignerOverlay.css';

type DesignerWorkspace = 'copilot' | 'source';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-900 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

const ENTITY_COLOR_CLASSES: Record<string, { bg: string; text: string; ring: string }> = {
  indigo: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', ring: 'ring-indigo-500/50' },
  emerald: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', ring: 'ring-emerald-500/50' },
  amber: { bg: 'bg-amber-500/20', text: 'text-amber-400', ring: 'ring-amber-500/50' },
  rose: { bg: 'bg-rose-500/20', text: 'text-rose-400', ring: 'ring-rose-500/50' },
  cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', ring: 'ring-cyan-500/50' },
  violet: { bg: 'bg-violet-500/20', text: 'text-violet-400', ring: 'ring-violet-500/50' },
  orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', ring: 'ring-orange-500/50' },
  teal: { bg: 'bg-teal-500/20', text: 'text-teal-400', ring: 'ring-teal-500/50' },
};

const DEFAULT_HTML = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: system-ui, sans-serif; padding: 24px; color: #1e293b; font-size: 13px; }
    h1 { color: #4f46e5; margin-bottom: 6px; font-size: 20px; }
    .subtitle { color: #64748b; margin-bottom: 16px; font-size: 12px; }
    .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0; }
    .metric-card {
      background: linear-gradient(135deg, #eef2ff, #f5f3ff);
      border-radius: 8px;
      padding: 14px;
      border: 1px solid #e0e7ff;
    }
    .metric-label { font-size: 10px; color: #6366f1; text-transform: uppercase; letter-spacing: 0.05em; }
    .metric-value { font-size: 22px; font-weight: 700; color: #312e81; margin-top: 2px; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 11px; }
  </style>
</head>
<body>
  <h1>{conversationStats.title}</h1>
  <p class="subtitle">Reporting period: {conversationStats.period}</p>
  <div class="metrics">
    <div class="metric-card">
      <div class="metric-label">Total Sessions</div>
      <div class="metric-value">{conversationStats.metrics.totalSessions}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Avg Duration</div>
      <div class="metric-value">{conversationStats.metrics.avgDuration}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Satisfaction</div>
      <div class="metric-value">{conversationStats.metrics.satisfaction} ★</div>
    </div>
  </div>
  <div class="footer">Generated for agent: {agentInfo.name} · {agentInfo.environment}</div>
</body>
</html>`;

const DEFAULT_ENTITIES: ReportEntity[] = [
  {
    id: '1',
    name: 'conversationStats',
    color: 'indigo',
    data: {
      title: 'Conversation Analytics',
      period: 'Last 30 days',
      metrics: { totalSessions: 1240, avgDuration: '4m 32s', satisfaction: 4.7 },
    },
  },
  {
    id: '2',
    name: 'agentInfo',
    color: 'emerald',
    data: { name: 'Support Bot', environment: 'Production', version: '2.1.0' },
  },
];

const UNSAVED_EXIT_MESSAGE =
  'You have unsaved changes. Leave without saving? Your work will be lost.';

function serializeDesignerSnapshot(
  reportName: string,
  parts: TemplateParts,
  settings: ReportSettings
): string {
  return JSON.stringify({ reportName, parts, settings });
}

function hasDesignerChanges(
  baseline: string | null,
  reportName: string,
  parts: TemplateParts,
  settings: ReportSettings
): boolean {
  if (baseline === null) return false;
  return baseline !== serializeDesignerSnapshot(reportName, parts, settings);
}

const FINALIZE_STATUS_LINES = {
  saving: [
    'Syncing your analytics blueprint…',
    'Validating report structure…',
    'Securing dashboard configuration…',
  ],
  converting: [
    'Translating components with AI…',
    'Optimizing analytics layout…',
    'Composing interactive visualizations…',
  ],
  publishing: [
    'Finalizing Analytics Hub deployment…',
    'Polishing live dashboard output…',
    'Preparing your analytics experience…',
  ],
} as const;

function getFinalizeHeadline(publishing: boolean, publishPhase: 'converting' | 'saving'): string {
  if (!publishing) return 'AI is saving your analytics';
  if (publishPhase === 'converting') return 'AI is composing analytics';
  return 'AI is finalizing Analytics Hub';
}

interface ReportFinalizeOverlayProps {
  publishing: boolean;
  publishPhase: 'converting' | 'saving';
  messageIndex: number;
  colors: ThemeColors;
}

function ReportFinalizeOverlay({
  publishing,
  publishPhase,
  messageIndex,
  colors,
}: ReportFinalizeOverlayProps) {
  const statusKey = publishing
    ? publishPhase === 'converting'
      ? 'converting'
      : 'publishing'
    : 'saving';
  const statusLines = FINALIZE_STATUS_LINES[statusKey];
  const statusLine = statusLines[messageIndex % statusLines.length];
  const headline = getFinalizeHeadline(publishing, publishPhase);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/55 backdrop-blur-md">
      <div
        className={`relative flex flex-col items-center gap-5 px-10 py-8 rounded-2xl border shadow-2xl ah-finalize-glow overflow-hidden min-w-[320px] max-w-[360px] ${colors.border} ${colors.primary}`}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px ah-finalize-shimmer opacity-80" />

        <div className="relative w-[4.5rem] h-[4.5rem]">
          <div className="absolute inset-0 rounded-full border border-violet-500/20" />
          <div className="absolute inset-1 rounded-full border border-indigo-500/15" />
          <div className="absolute inset-0 ah-finalize-orbit">
            <span className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-violet-400 shadow-[0_0_8px_rgba(167,139,250,0.8)]" />
          </div>
          <div className="absolute inset-0 ah-finalize-orbit-reverse">
            <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-indigo-400 shadow-[0_0_6px_rgba(129,140,248,0.8)]" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="absolute inset-3 rounded-full bg-gradient-to-br from-indigo-500/25 via-violet-500/20 to-fuchsia-500/15 animate-pulse" />
            <Sparkles className="relative w-8 h-8 text-violet-300 ah-finalize-sparkle drop-shadow-[0_0_10px_rgba(167,139,250,0.6)]" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <p className={`text-sm font-semibold tracking-tight ${colors.text.primary}`}>
            {headline}
          </p>
          <div className="flex items-center justify-center gap-1.5 h-4">
            <span className="w-1.5 h-1.5 rounded-full bg-violet-400 ah-finalize-dot" />
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 ah-finalize-dot" />
            <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400 ah-finalize-dot" />
          </div>
          <p
            key={`${statusKey}-${messageIndex}`}
            className={`text-[11px] ${colors.text.muted} ah-finalize-message min-h-[1rem]`}
          >
            {statusLine}
          </p>
        </div>

        <div className="w-full h-1 rounded-full bg-neutral-700/60 overflow-hidden">
          <div className="h-full w-full ah-finalize-shimmer rounded-full opacity-90" />
        </div>

        <div className="flex items-center gap-1.5 text-[10px] text-violet-400/80">
          <Bot className="w-3 h-3" />
          <span>Powered by SelficAI</span>
        </div>
      </div>
    </div>
  );
}

interface ReportDesignerModalProps {
  theme?: Theme;
  report?: ReportDefinition | null;
  collectionId: string;
  onClose: () => void;
  onSave?: (report: Partial<ReportDefinition>) => void;
}

const ReportDesignerModal: React.FC<ReportDesignerModalProps> = ({
  theme = 'dark',
  report,
  collectionId,
  onClose,
  onSave,
}) => {
  const colors = THEME_CONFIG[theme];
  const codeEditorRef = useRef<ReportCodeEditorHandle>(null);
  const isNewReport = !report?.id;
  const { showToast } = useToast();

  const [reportLoading, setReportLoading] = useState(!!report?.id);
  const [reportLoadError, setReportLoadError] = useState<string | null>(null);
  const [reportName, setReportName] = useState(report?.name ?? 'Untitled Report');
  const [templateParts, setTemplateParts] = useState<TemplateParts>(() =>
    isNewReport
      ? splitTemplate(DEFAULT_HTML)
      : { html: '', css: '', js: '' }
  );
  const [entities, setEntities] = useState<ReportEntity[]>([]);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ReportSettings>(() => ({
    ...createDefaultReportSettings(),
    ...(report?.settings ?? {}),
    reportHeader: report?.settings?.reportHeader ?? { title: '', subtitle: '' },
    copilotIds: report?.settings?.copilotIds ?? [],
  }));
  const [settingsGatePassed, setSettingsGatePassed] = useState(false);
  const [pendingSaveAfterSettings, setPendingSaveAfterSettings] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'split' | 'editor' | 'preview'>('split');
  const [workspace, setWorkspace] = useState<DesignerWorkspace>(isNewReport ? 'copilot' : 'source');
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [editingEntity, setEditingEntity] = useState<ReportEntity | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [reportDbId, setReportDbId] = useState<string | undefined>(report?.id);
  const [canPublish, setCanPublish] = useState(!!report?.id);
  const [publishing, setPublishing] = useState(false);
  const [publishPhase, setPublishPhase] = useState<'converting' | 'saving'>('converting');
  const [previewParts, setPreviewParts] = useState(templateParts);
  const [copilotDraft, setCopilotDraft] = useState<{
    parts: TemplateParts;
    entities: ReportEntity[];
  } | null>(null);
  const [previewMaximized, setPreviewMaximized] = useState(false);
  const [finalizeMessageIndex, setFinalizeMessageIndex] = useState(0);
  const savedSnapshotRef = useRef<string | null>(null);
  const initialBaselineSetRef = useRef(false);
  const [snapshotTick, setSnapshotTick] = useState(0);

  const commitSavedSnapshot = useCallback(
    (name: string, parts: TemplateParts, nextSettings: ReportSettings) => {
      savedSnapshotRef.current = serializeDesignerSnapshot(name, parts, nextSettings);
      setSnapshotTick((tick) => tick + 1);
    },
    []
  );

  const hasUnsavedChanges = useMemo(() => {
    if (reportLoading) return false;
    return hasDesignerChanges(savedSnapshotRef.current, reportName, templateParts, settings);
  }, [reportLoading, reportName, templateParts, settings, snapshotTick]);

  const handleRequestClose = useCallback(() => {
    const parts = codeEditorRef.current?.getParts() ?? templateParts;
    if (hasDesignerChanges(savedSnapshotRef.current, reportName, parts, settings)) {
      if (!window.confirm(UNSAVED_EXIT_MESSAGE)) return;
    }
    onClose();
  }, [onClose, reportName, templateParts, settings]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (report?.id || reportLoading || initialBaselineSetRef.current) return;
    initialBaselineSetRef.current = true;
    commitSavedSnapshot(reportName, templateParts, settings);
  }, [report?.id, reportLoading, reportName, templateParts, settings, commitSavedSnapshot]);

  useEffect(() => {
    if (!savingReport && !publishing) return;

    setFinalizeMessageIndex(0);
    const intervalId = window.setInterval(() => {
      setFinalizeMessageIndex((prev) => prev + 1);
    }, 2200);

    return () => window.clearInterval(intervalId);
  }, [savingReport, publishing, publishPhase]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewMaximized) {
        setPreviewMaximized(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [previewMaximized]);

  useEffect(() => {
    const timer = window.setTimeout(() => setPreviewParts(templateParts), 400);
    return () => window.clearTimeout(timer);
  }, [templateParts]);

  useEffect(() => {
    if (!report?.id) return;

    let cancelled = false;

    const loadReportForEdit = async () => {
      setReportLoading(true);
      setReportLoadError(null);

      try {
        const response = await analyticsHubService.getReportDefinitionById(report.id);
        if (cancelled) return;

        const api = response.data;
        const parts = templatePartsFromAnalyticsHubReport(api);
        const mapped = mapAnalyticsHubReportDefinition(api, collectionId);

        setReportName(mapped.name);
        setTemplateParts(parts);
        setPreviewParts(parts);
        setSettings(mapped.settings ?? createDefaultReportSettings());
        setReportDbId(mapped.id || report.id);
        setCanPublish(true);
        setSettingsGatePassed(true);
        setWorkspace('source');
        commitSavedSnapshot(
          mapped.name,
          parts,
          mapped.settings ?? createDefaultReportSettings()
        );
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load report. Please try again.';
        setReportLoadError(message);
        showToast({ message, type: 'error', duration: 6000 });
      } finally {
        if (!cancelled) setReportLoading(false);
      }
    };

    void loadReportForEdit();
    return () => { cancelled = true; };
  }, [report?.id, collectionId, showToast, commitSavedSnapshot]);

  const loadEntities = useCallback(async () => {
    if (reportLoading) return;
    if (!collectionId) {
      setEntities(report?.entities?.length ? report.entities : DEFAULT_ENTITIES);
      setSelectedEntityId(report?.entities?.[0]?.id ?? DEFAULT_ENTITIES[0]?.id ?? null);
      setEntitiesLoading(false);
      return;
    }

    setEntitiesLoading(true);
    setEntitiesError(null);

    try {
      const response = await analyticsHubService.getEntities(collectionId);
      const mapped = (response.data ?? []).map((apiEntity, index) => ({
        ...mapAnalyticsHubEntity(apiEntity),
        color: getEntityColor(index),
      }));
      setEntities(mapped);
      setSelectedEntityId((current) => {
        if (current && mapped.some((e) => e.id === current)) return current;
        return mapped[0]?.id ?? null;
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load entities. Please try again.';
      setEntitiesError(message);
      setEntities([]);
      setSelectedEntityId(null);
    } finally {
      setEntitiesLoading(false);
    }
  }, [collectionId, report?.entities, reportLoading]);

  useEffect(() => {
    void loadEntities();
  }, [loadEntities]);

  const selectedEntity = entities.find((e) => e.id === selectedEntityId) ?? null;

  const htmlTemplate = useMemo(
    () => combineTemplateParts(templateParts),
    [templateParts]
  );

  const previewHtml = useMemo(
    () => buildReportPreviewHtml(previewParts, entities),
    [previewParts, entities]
  );

  const copilotPreviewHtml = useMemo(() => {
    if (copilotDraft) {
      return buildReportPreviewHtml(copilotDraft.parts, copilotDraft.entities);
    }
    return previewHtml;
  }, [copilotDraft, previewHtml]);

  const analyticsHubViewUrl = useMemo(() => {
    if (!reportDbId?.trim()) return null;
    return getReportAnalyticsHubViewUrl({ id: reportDbId });
  }, [reportDbId]);

  const fullscreenPreviewHtml = workspace === 'copilot' ? copilotPreviewHtml : previewHtml;
  const fullscreenPreviewHint =
    workspace === 'copilot'
      ? copilotDraft
        ? 'Copilot draft · live preview'
        : 'Current report'
      : 'Interpolated';

  const insertPlaceholder = useCallback((placeholder: string) => {
    codeEditorRef.current?.insertAtCursor(placeholder);
  }, []);

  const handleDeleteEntity = (id: string) => {
    setEntities((prev) => prev.filter((e) => e.id !== id));
    if (selectedEntityId === id) {
      setSelectedEntityId(entities.find((e) => e.id !== id)?.id ?? null);
    }
  };

  const handleCopyPath = (path: string) => {
    navigator.clipboard.writeText(formatEntityPlaceholder(path));
    setCopiedPath(path);
    setTimeout(() => setCopiedPath(null), 1500);
  };

  const applyTemplate = (template: ReportTemplate) => {
    setTemplateParts(splitTemplate(template.htmlTemplate));
    if (isNewReport || reportName === 'Untitled Report') {
      setReportName(template.suggestedName);
    }
    setSettings((prev) => ({
      ...prev,
      description: prev.description || template.description,
      icon: template.icon,
    }));
  };

  const openTemplatePicker = () => {
    const isModified = htmlTemplate !== combineTemplateParts(splitTemplate(DEFAULT_HTML));
    if ((!isNewReport || isModified) &&
        !window.confirm('Loading a template will replace your current HTML. Continue?')) {
      return;
    }
    setShowTemplatePicker(true);
  };

  const submitReportToApi = useCallback(
    async (
      nextSettings: ReportSettings,
      options?: { publish?: boolean }
    ): Promise<boolean> => {
      const tenantId = localStorage.getItem('tenant_id');
      if (!tenantId) {
        showToast({ message: 'Tenant not found. Please sign in again.', type: 'error' });
        return false;
      }

      const isCreate = !reportDbId;
      if (options?.publish) {
        setPublishing(true);
        setPublishPhase('converting');
      } else {
        setSavingReport(true);
      }

      try {
        const latestParts = codeEditorRef.current?.getParts() ?? templateParts;
        const latestHtml = combineTemplateParts(latestParts);
        const activeSettings = options?.publish
          ? { ...nextSettings, isActive: true }
          : nextSettings;

        let finalReportTsx: string | undefined;
        let reportEntities: string | undefined;

        if (options?.publish) {
          const conversion = await analyticsHubService.convertJsxToTsx({
            jsx: latestParts.html,
            css: latestParts.css,
            js: latestParts.js,
          });

          const converted = resolveJsxToTsxConversion(conversion);
          if (!converted) {
            throw new Error(conversion.message || 'JSX to TSX conversion failed');
          }

          finalReportTsx = converted.tsx;
          reportEntities = converted.entities;
          setPublishPhase('saving');
        }

        const response = await analyticsHubService.upsertReportDefinition(
          buildReportDefinitionUpsertPayload({
            reportDbId,
            reportName,
            collectionId,
            settings: activeSettings,
            parts: latestParts,
            isCreate,
            finalReportTsx,
            entities: reportEntities,
          })
        );

        const savedId = resolveReportDbIdFromUpsertResponse(response, reportDbId);

        if (savedId) setReportDbId(savedId);
        setCanPublish(true);
        setSettings(activeSettings);
        setSettingsGatePassed(true);
        setTemplateParts(latestParts);
        setPreviewParts(latestParts);
        commitSavedSnapshot(reportName, latestParts, activeSettings);
        onSave?.({
          name: reportName,
          description: activeSettings.description,
          icon: activeSettings.icon,
          htmlTemplate: latestHtml,
          entities,
          settings: activeSettings,
          status: activeSettings.isActive ? 'ready' : 'draft',
        });

        if (options?.publish) {
          showToast({
            message: `"${reportName}" published successfully.`,
            type: 'success',
            duration: 5000,
          });
        } else {
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
          setShowSettingsModal(false);
          setPendingSaveAfterSettings(false);
          showToast({
            message: `"${reportName}" saved successfully.`,
            type: 'success',
            duration: 5000,
          });
        }
        return true;
      } catch (err) {
        showToast({
          message: err instanceof Error ? err.message : `Failed to ${options?.publish ? 'publish' : 'save'} report. Please try again.`,
          type: 'error',
          duration: 6000,
        });
        return false;
      } finally {
        if (options?.publish) {
          setPublishing(false);
        } else {
          setSavingReport(false);
        }
      }
    },
    [collectionId, commitSavedSnapshot, entities, onSave, reportDbId, reportName, showToast, templateParts]
  );

  const handleSaveReport = () => {
    if (!settingsGatePassed) {
      setPendingSaveAfterSettings(true);
      setShowSettingsModal(true);
      return;
    }
    void submitReportToApi(settings);
  };

  const handlePublishReport = () => {
    if (!canPublish) return;
    void submitReportToApi(settings, { publish: true });
  };

  const handleSettingsModalSave = async (nextSettings: ReportSettings) => {
    if (pendingSaveAfterSettings) {
      await submitReportToApi(nextSettings);
      return;
    }
    setSettings(nextSettings);
    setShowSettingsModal(false);
  };

  const handleSettingsModalClose = () => {
    if (savingReport) return;
    setShowSettingsModal(false);
    setPendingSaveAfterSettings(false);
  };

  const handleCopilotApply = useCallback(
    (
      result: { parts: TemplateParts; entities: ReportEntity[]; suggestedName?: string },
      options?: { navigateToSource?: boolean }
    ) => {
      setTemplateParts(result.parts);
      setPreviewParts(result.parts);
      setEntities(result.entities);
      setSelectedEntityId(result.entities[0]?.id ?? null);
      setCopilotDraft(null);
      if (result.suggestedName && (isNewReport || reportName === 'Untitled Report')) {
        setReportName(result.suggestedName);
      }
      setShowTemplatePicker(false);
      if (options?.navigateToSource) {
        setWorkspace('source');
      }
    },
    [isNewReport, reportName]
  );

  const handleCopilotPreviewDraft = useCallback(
    (draft: { parts: TemplateParts; entities: ReportEntity[] }) => {
      setCopilotDraft(draft);
    },
    []
  );

  const renderPreviewPane = (html: string, hint?: string) => (
    <div className="flex flex-col min-w-0 min-h-0 flex-1">
      <div className={`flex items-center justify-between px-3 py-1.5 border-b ${colors.border} ${colors.secondary}`}>
        <span className={`text-[10px] font-medium ${colors.text.secondary} flex items-center gap-1`}>
          <Eye className="w-3 h-3 text-emerald-400" />
          Preview
        </span>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`text-[10px] ${colors.text.muted} truncate`}>{hint ?? 'Interpolated'}</span>
          <button
            type="button"
            onClick={() => setPreviewMaximized(true)}
            className={`p-1 rounded ${colors.hover} ${colors.text.muted} hover:text-indigo-400 transition-colors flex-shrink-0`}
            title="Maximize preview"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <div className={`flex-1 overflow-auto min-h-0 ${theme === 'dark' ? 'bg-white' : 'bg-gray-50'}`}>
        <iframe
          title="Report preview"
          srcDoc={html}
          className="w-full h-full border-0 min-h-[300px]"
          sandbox="allow-same-origin allow-scripts"
        />
      </div>
    </div>
  );

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col ${colors.background}`}>
      {(savingReport || publishing) && (
        <ReportFinalizeOverlay
          publishing={publishing}
          publishPhase={publishPhase}
          messageIndex={finalizeMessageIndex}
          colors={colors}
        />
      )}

      {/* Top bar */}
      <div className={`flex-shrink-0 border-b ${colors.border} ${colors.primary}`}>
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                <LayoutTemplate className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <input
                  type="text"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  className={`text-xs font-semibold bg-transparent border-none focus:outline-none ${colors.text.primary} w-48`}
                />
                <p className={`text-[10px] ${colors.text.muted}`}>Report Designer</p>
              </div>
            </div>

            <div className={`hidden sm:flex items-center gap-0.5 p-0.5 rounded-md ${colors.secondary} border ${colors.border}`}>
              {(
                [
                  { id: 'copilot' as const, label: 'Copilot', icon: Bot },
                  { id: 'source' as const, label: 'Source', icon: Code2 },
                ] as const
              ).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setWorkspace(id)}
                  className={`px-2.5 py-1 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                    workspace === id
                      ? id === 'copilot'
                        ? 'bg-violet-600 text-white'
                        : 'bg-indigo-600 text-white'
                      : `${colors.text.muted} ${colors.hover}`
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {label}
                  {id === 'copilot' && workspace !== 'copilot' && (
                    <Sparkles className="w-2.5 h-2.5 text-violet-400" />
                  )}
                </button>
              ))}
            </div>

            {workspace === 'source' && (
              <div className={`hidden md:flex items-center gap-0.5 p-0.5 rounded-md ${colors.secondary} border ${colors.border}`}>
                {(['editor', 'split', 'preview'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setViewMode(mode)}
                    className={`px-2 py-1 rounded text-[10px] font-medium capitalize transition-colors flex items-center gap-1 ${
                      viewMode === mode
                        ? 'bg-indigo-600 text-white'
                        : `${colors.text.muted} ${colors.hover}`
                    }`}
                  >
                    {mode === 'editor' && <Code2 className="w-3 h-3" />}
                    {mode === 'split' && <Layers className="w-3 h-3" />}
                    {mode === 'preview' && <Eye className="w-3 h-3" />}
                    {mode}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {analyticsHubViewUrl && (
              <button
                type="button"
                onClick={() => window.open(analyticsHubViewUrl, '_blank', 'noopener,noreferrer')}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-sky-500/30 bg-sky-600/10 hover:bg-sky-600/20 text-sky-400 text-[10px] font-medium transition-colors"
                title={`View report: ${analyticsHubViewUrl}`}
              >
                <Eye className="w-3.5 h-3.5" />
                View
              </button>
            )}
            <button
              type="button"
              onClick={openTemplatePicker}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md border ${colors.border} ${colors.hover} ${colors.text.secondary} text-[10px] font-medium transition-colors`}
              title="Browse templates"
            >
              <LayoutGrid className="w-3.5 h-3.5 text-indigo-400" />
              Templates
            </button>
            <button
              type="button"
              onClick={() => setShowSettingsModal(true)}
              className={`p-1.5 rounded-md border ${colors.border} ${colors.hover} ${colors.text.muted} transition-colors`}
              title="Report settings"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={handleSaveReport}
              disabled={savingReport || publishing || reportLoading || !!reportLoadError}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all disabled:opacity-60 ${
                saved ? 'bg-emerald-600 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}
            >
              {saved ? <Check className="w-3 h-3" /> : <Save className="w-3 h-3" />}
              {savingReport ? 'Saving…' : saved ? 'Saved' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handlePublishReport}
              disabled={!canPublish || savingReport || publishing || reportLoading || !!reportLoadError}
              title={canPublish ? 'Publish report' : 'Save the report first to enable publish'}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-violet-600 hover:bg-violet-700 disabled:hover:bg-violet-600 text-white"
            >
              <UploadCloud className="w-3 h-3" />
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
            <button
              type="button"
              onClick={handleRequestClose}
              className={`p-1.5 rounded-md ${colors.hover} ${colors.text.muted}`}
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative">
        {reportLoading && (
          <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 ${colors.background}`}>
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
            <p className={`text-sm ${colors.text.secondary}`}>Loading report…</p>
          </div>
        )}

        {!reportLoading && reportLoadError && (
          <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 ${colors.background} px-6`}>
            <AlertCircle className="w-8 h-8 text-red-400" />
            <p className={`text-sm text-red-400 text-center max-w-md`}>{reportLoadError}</p>
            <button
              type="button"
              onClick={handleRequestClose}
              className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-medium"
            >
              Close
            </button>
          </div>
        )}

        {!reportLoading && !reportLoadError && (
        <>
        {/* Entity sidebar */}
        <aside className={`w-56 flex-shrink-0 border-r ${colors.border} ${colors.primary} flex flex-col`}>
          <div className={`px-3 py-2 border-b ${colors.border}`}>
            <div className="flex items-center justify-between">
              <h3 className={`text-[11px] font-semibold ${colors.text.primary} flex items-center gap-1.5`}>
                <Database className="w-3 h-3 text-indigo-400" />
                Entities
              </h3>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${colors.secondary} ${colors.text.muted}`}>
                {entities.length}
              </span>
            </div>
            <p className={`text-[10px] ${colors.text.muted} mt-0.5`}>
              Use <code className="text-indigo-400">{`{entity.field}`}</code>
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {entitiesLoading && (
              <div className={`flex items-center justify-center gap-2 py-8 ${colors.text.muted}`}>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-[10px]">Loading entities…</span>
              </div>
            )}

            {!entitiesLoading && entitiesError && (
              <div className="px-1 py-3 space-y-2">
                <div className="flex items-start gap-1.5 text-[10px] text-red-400">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{entitiesError}</span>
                </div>
                <button
                  type="button"
                  onClick={() => void loadEntities()}
                  className="w-full px-2 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] font-medium"
                >
                  Retry
                </button>
              </div>
            )}

            {!entitiesLoading && !entitiesError && entities.map((entity) => {
              const colorCls = ENTITY_COLOR_CLASSES[entity.color] ?? ENTITY_COLOR_CLASSES.indigo;
              const isSelected = selectedEntityId === entity.id;
              return (
                <div
                  key={entity.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedEntityId(entity.id)}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedEntityId(entity.id)}
                  className={`w-full text-left rounded-lg p-2 border transition-all group cursor-pointer ${
                    isSelected
                      ? `${colors.surface} border-indigo-500/50 ring-1 ${colorCls.ring}`
                      : `${colors.surface} ${colors.border} ${colors.hover} border-transparent`
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-6 h-6 rounded-md ${colorCls.bg} flex items-center justify-center flex-shrink-0`}>
                      <Braces className={`w-3 h-3 ${colorCls.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[11px] font-medium truncate ${colors.text.primary}`}>{entity.name}</p>
                      <p className={`text-[10px] ${colors.text.muted}`}>
                        {Object.keys(entity.data).length} fields
                      </p>
                    </div>
                    <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setEditingEntity(entity); setShowEntityModal(true); }}
                        className={`p-0.5 rounded ${colors.hover} ${colors.text.muted}`}
                      >
                        <Pencil className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleDeleteEntity(entity.id); }}
                        className="p-0.5 rounded hover:bg-red-500/20 text-neutral-400 hover:text-red-400"
                      >
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            {!entitiesLoading && entities.length === 0 && !entitiesError && (
              <div className={`text-center py-6 px-3 rounded-lg border border-dashed ${colors.border}`}>
                <Database className={`w-5 h-5 mx-auto mb-1 ${colors.text.muted}`} />
                <p className={`text-[10px] ${colors.text.muted}`}>No entities</p>
              </div>
            )}
          </div>

          <div className={`p-2 border-t ${colors.border}`}>
            <button
              type="button"
              onClick={() => { setEditingEntity(null); setShowEntityModal(true); }}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600/15 hover:bg-indigo-600/25 text-indigo-400 text-[11px] font-medium border border-indigo-500/25 transition-colors"
            >
              <Plus className="w-3 h-3" />
              New Entity
            </button>
          </div>

          {selectedEntity && (
            <div className={`border-t ${colors.border} max-h-36 overflow-y-auto`}>
              <div className={`px-3 py-1.5 ${colors.secondary}`}>
                <p className={`text-[10px] font-medium ${colors.text.secondary}`}>Bindings</p>
              </div>
              <div className="p-1.5 space-y-0.5">
                {flattenEntityPaths(selectedEntity.data, selectedEntity.name).map((path) => (
                  <div
                    key={path}
                    role="button"
                    tabIndex={0}
                    onClick={() => insertPlaceholder(formatEntityPlaceholder(path))}
                    onKeyDown={(e) => e.key === 'Enter' && insertPlaceholder(formatEntityPlaceholder(path))}
                    className={`w-full flex items-center gap-1.5 px-1.5 py-1 rounded text-left text-[10px] font-mono ${colors.hover} group cursor-pointer`}
                  >
                    <ChevronRight className={`w-2.5 h-2.5 ${colors.text.muted}`} />
                    <span className="flex-1 truncate text-indigo-400">{path}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleCopyPath(path); }}
                      className={`p-0.5 rounded opacity-0 group-hover:opacity-100 ${colors.text.muted}`}
                    >
                      {copiedPath === path ? <Check className="w-2.5 h-2.5 text-emerald-400" /> : <Copy className="w-2.5 h-2.5" />}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Copilot / Editor + Preview */}
        <div className="flex-1 flex min-w-0 min-h-0">
          {workspace === 'copilot' ? (
            <>
              <div className={`w-1/2 min-w-0 min-h-0 border-r ${colors.border}`}>
                <ReportCopilotPanel
                  theme={theme}
                  onApply={handleCopilotApply}
                  onPreviewDraft={handleCopilotPreviewDraft}
                  onSwitchToSource={() => setWorkspace('source')}
                />
              </div>
              {renderPreviewPane(
                copilotPreviewHtml,
                copilotDraft ? 'Copilot draft · live preview' : 'Current report'
              )}
            </>
          ) : (
            <>
              {(viewMode === 'editor' || viewMode === 'split') && (
                <div
                  className={`flex flex-col min-w-0 min-h-0 ${viewMode === 'split' ? 'w-1/2 border-r' : 'flex-1'} ${colors.border}`}
                  style={{ color: '#1e293b' }}
                >
                  <ReportCodeEditor
                    ref={codeEditorRef}
                    theme="light"
                    parts={templateParts}
                    onChange={setTemplateParts}
                  />
                </div>
              )}

              {(viewMode === 'preview' || viewMode === 'split') &&
                renderPreviewPane(previewHtml)}
            </>
          )}
        </div>
        </>
        )}
      </div>

      {showEntityModal && (
        <EntityModal
          theme={theme}
          entity={editingEntity}
          collectionId={collectionId}
          onClose={() => { setShowEntityModal(false); setEditingEntity(null); }}
          onSaved={() => {
            setEditingEntity(null);
            void loadEntities();
          }}
        />
      )}

      {showSettingsModal && (
        <ReportSettingsModal
          theme={theme}
          settings={settings}
          onClose={handleSettingsModalClose}
          onSave={handleSettingsModalSave}
          primaryActionLabel={pendingSaveAfterSettings ? 'Save Report' : 'Apply Settings'}
          saving={savingReport}
          subtitle={
            pendingSaveAfterSettings
              ? 'Complete settings to publish your report'
              : 'Configure metadata, access & data source'
          }
        />
      )}

      {showTemplatePicker && (
        <ReportTemplatePickerModal
          theme={theme}
          onClose={() => setShowTemplatePicker(false)}
          onApply={applyTemplate}
        />
      )}

      {previewMaximized && (
        <div
          className={`fixed inset-0 z-[70] flex flex-col ${colors.background}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex-shrink-0 flex items-center justify-between px-4 py-2.5 border-b ${colors.border} ${colors.primary}`}>
            <div className="flex items-center gap-2 min-w-0">
              <Eye className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className={`text-xs font-semibold ${colors.text.primary}`}>Report Preview</p>
                <p className={`text-[10px] ${colors.text.muted} truncate`}>{fullscreenPreviewHint}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPreviewMaximized(false)}
              className={`p-2 rounded-lg ${colors.hover} ${colors.text.muted} hover:text-indigo-400 transition-colors`}
              title="Exit fullscreen"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
          </div>
          <div className={`flex-1 min-h-0 overflow-hidden ${theme === 'dark' ? 'bg-white' : 'bg-gray-50'}`}>
            <iframe
              title="Report preview fullscreen"
              srcDoc={fullscreenPreviewHtml}
              className="w-full h-full border-0"
              sandbox="allow-same-origin allow-scripts"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDesignerModal;
